import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { LanguageContext, useLanguage } from '@/i18n/language-context';
import { isLocale, type Locale } from '@/i18n/translations';
import { currentSession, setAccountLocale, useSession } from './session';

type Save = {
  userId: string;
  epoch: number;
  pending: number;
  latest: number;
};
type Status = { owner: Save; state: 'saving' | 'saved' | 'error' } | null;

/** Account persistence is an adapter; the base language provider remains browser-only. */
export function AccountLanguageProvider({ children }: { children: ReactNode }): JSX.Element {
  const language = useLanguage();
  const { setLocale: setBrowserLocale } = language;
  const session = useSession();
  const userId = session.phase === 'signedIn' ? session.profile?.user.id : undefined;
  const epoch = session.epoch;
  const savedLocale = session.profile?.preferences.locale;
  const work = useRef<Save | null>(null);
  const [status, setStatus] = useState<Status>(null);
  const adopted = useRef<{ userId: string | undefined; epoch: number }>();

  useEffect(() => {
    if (!adopted.current || adopted.current.userId !== userId || adopted.current.epoch !== epoch) {
      adopted.current = { userId, epoch };
      work.current = null;
      setStatus(null);
      if (userId && isLocale(savedLocale)) setBrowserLocale(savedLocale);
      return;
    }
    if (
      userId &&
      isLocale(savedLocale) &&
      !work.current?.pending &&
      !(status?.owner === work.current && status?.state === 'error')
    )
      setBrowserLocale(savedLocale);
  }, [userId, epoch, savedLocale, status, setBrowserLocale]);

  const chooseLocale = useCallback(
    (next: Locale): void => {
      setBrowserLocale(next);
      const active = currentSession();
      if (
        !userId ||
        active.phase !== 'signedIn' ||
        active.profile?.user.id !== userId ||
        active.epoch !== epoch
      )
        return;
      const owner =
        work.current?.userId === userId && work.current.epoch === epoch
          ? work.current
          : { userId, epoch, pending: 0, latest: 0 };
      work.current = owner;
      const operation = ++owner.latest;
      owner.pending++;
      setStatus({ owner, state: 'saving' });
      const finish = (state: 'saved' | 'error'): void => {
        owner.pending--;
        if (work.current === owner && operation === owner.latest) setStatus({ owner, state });
      };
      void setAccountLocale(next, userId, epoch).then(
        () => finish('saved'),
        () => finish('error'),
      );
    },
    [setBrowserLocale, userId, epoch],
  );

  const visibleStatus =
    status?.owner.userId === userId &&
    status?.owner.epoch === epoch &&
    status?.owner === work.current
      ? (status?.state ?? 'saved')
      : 'saved';

  return (
    <LanguageContext.Provider
      value={{
        ...language,
        setLocale: chooseLocale,
        ...(userId
          ? {
              persistence: {
                state: visibleStatus,
                retry: () => chooseLocale(language.locale),
              },
            }
          : {}),
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}
